import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ScanSearch, Wifi, HardDrive, Monitor, Users, Shield, Clock, Package, Cpu, MemoryStick, Database } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { discoveryBadgeColor } from '@/hooks/useDiscoveryMap';

interface AtivoDiscoveryProps {
  ativoId: string;
}

function useAtivoDiscovery(ativoId: string) {
  return useQuery({
    queryKey: ['ativo_discovery', ativoId],
    queryFn: async () => {
      const [mapResult, snapshotsResult, ativoResult] = await Promise.all([
        supabase
          .from('discovery_device_map')
          .select('device_id, last_seen_at, first_seen_at, agent_version, wmi_method')
          .eq('ativo_id', ativoId),
        supabase
          .from('discovery_snapshots')
          .select(
            'id, collected_at, agent_version, payload_size_kb, queries_total, queries_success, queries_failed, status, services, hotfixes, local_users, physical_disks, network_detail, tcp_listening'
          )
          .eq('ativo_id', ativoId)
          .order('collected_at', { ascending: false })
          .limit(30),
        supabase
          .from('cmdb_ativos')
          .select('capacidade, sistema_operacional')
          .eq('id', ativoId)
          .maybeSingle(),
      ]);

      return {
        devices: mapResult.data ?? [],
        snapshots: snapshotsResult.data ?? [],
        capacidade: (ativoResult.data?.capacidade as Record<string, unknown>) ?? null,
        sistemaOperacional: ativoResult.data?.sistema_operacional ?? null,
      };
    },
    enabled: !!ativoId,
  });
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CountBadge({ items }: { items: unknown }) {
  const count = Array.isArray(items) ? items.length : 0;
  return <span className="text-sm text-gray-500">{count} item(s)</span>;
}

export const AtivoDiscovery = ({ ativoId }: AtivoDiscoveryProps) => {
  const { data, isLoading, error } = useAtivoDiscovery(ativoId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-500">
        <ScanSearch className="h-5 w-5 mr-2 animate-pulse" />
        Carregando dados de discovery...
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6 text-center text-red-500">
        Erro ao carregar dados de discovery.
      </div>
    );
  }

  if (!data || data.devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
        <ScanSearch className="h-10 w-10" />
        <p className="text-sm font-medium">Este ativo não foi descoberto pelo AROAN Discovery Agent.</p>
        <p className="text-xs">Instale o agente no dispositivo e aguarde a primeira coleta.</p>
      </div>
    );
  }

  const device = data.devices[0];
  const { color, label } = discoveryBadgeColor(device.last_seen_at);
  const latestSnapshot = data.snapshots[0];

  return (
    <div className="space-y-4">
      {/* Header card */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Último sync</p>
              <p className={`font-semibold flex items-center gap-1 ${color}`}>
                <Clock className="h-3.5 w-3.5" />
                {label}
              </p>
              <p className="text-xs text-gray-500">{fmtDate(device.last_seen_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Primeira coleta</p>
              <p className="text-gray-700">{fmtDate(device.first_seen_at)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Versão do agente</p>
              <p className="text-gray-700">{device.agent_version ?? '-'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Device ID</p>
              <p className="text-gray-700 font-mono text-xs">{device.device_id}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Total de coletas</p>
              <p className="text-gray-700">{data.snapshots.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Hardware specs */}
      {data.capacidade && (
        <Card>
          <CardHeader className="pb-2 pt-3 px-4">
            <CardTitle className="text-sm text-gray-700">Hardware detectado</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {data.capacidade.cpu_model && (
                <div className="flex items-start gap-2">
                  <Cpu className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">CPU</p>
                    <p className="text-gray-700 font-medium">{String(data.capacidade.cpu_model)}</p>
                    {data.capacidade.cpu_cores && (
                      <p className="text-xs text-gray-400">{String(data.capacidade.cpu_cores)} cores / {String(data.capacidade.cpu_threads ?? data.capacidade.cpu_cores)} threads</p>
                    )}
                  </div>
                </div>
              )}
              {data.capacidade.ram_gb && (
                <div className="flex items-start gap-2">
                  <MemoryStick className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Memória RAM</p>
                    <p className="text-gray-700 font-medium">{Number(data.capacidade.ram_gb).toFixed(1)} GB</p>
                  </div>
                </div>
              )}
              {data.capacidade.disk_total_gb && (
                <div className="flex items-start gap-2">
                  <Database className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Armazenamento</p>
                    <p className="text-gray-700 font-medium">{Number(data.capacidade.disk_total_gb).toFixed(0)} GB total</p>
                    {data.capacidade.disk_free_gb && (
                      <p className="text-xs text-gray-400">{Number(data.capacidade.disk_free_gb).toFixed(0)} GB livres</p>
                    )}
                  </div>
                </div>
              )}
              {data.sistemaOperacional && (
                <div className="flex items-start gap-2">
                  <Monitor className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Sistema Operacional</p>
                    <p className="text-gray-700 font-medium text-xs leading-snug">{data.sistemaOperacional}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Latest snapshot details */}
      {latestSnapshot && (
        <div className="grid grid-cols-2 gap-3">
          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Monitor className="h-4 w-4 text-blue-500" />
                Serviços Windows
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <CountBadge items={latestSnapshot.services} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Shield className="h-4 w-4 text-green-500" />
                Hotfixes / Patches
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <CountBadge items={latestSnapshot.hotfixes} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Users className="h-4 w-4 text-purple-500" />
                Usuários Locais
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <CountBadge items={latestSnapshot.local_users} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <HardDrive className="h-4 w-4 text-orange-500" />
                Discos Físicos
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <CountBadge items={latestSnapshot.physical_disks} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Wifi className="h-4 w-4 text-cyan-500" />
                Interfaces de Rede
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <CountBadge items={latestSnapshot.network_detail} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm flex items-center gap-1.5">
                <Package className="h-4 w-4 text-gray-500" />
                Coleta (queries)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              <span className="text-sm text-gray-500">
                {latestSnapshot.queries_success ?? '-'}/{latestSnapshot.queries_total ?? '-'} ok
                {latestSnapshot.payload_size_kb
                  ? ` · ${latestSnapshot.payload_size_kb} KB`
                  : ''}
              </span>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Snapshot history */}
      <Card>
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm">Histórico de Coletas (últimas 30)</CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {data.snapshots.map((snap) => (
              <div key={snap.id} className="flex items-center justify-between text-xs py-1 border-b border-gray-50 last:border-0">
                <span className="text-gray-600">{fmtDate(snap.collected_at)}</span>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">{snap.payload_size_kb ?? '-'} KB</span>
                  <Badge
                    variant={snap.status === 'success' ? 'default' : 'destructive'}
                    className="text-xs py-0 h-5"
                  >
                    {snap.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
